import { Stack } from "@mui/material";
import { CrremPathwayOrLegacyChart } from "features/crrem-pathway-chart/components/CrremPathwayOrLegacyChart";
import { EnergyAnalysisDashboard } from "features/energy-analysis-dashboard/components/EnergyAnalysisDashboard";
import { EnergyAnalysisUploadButton } from "features/energy-analysis-dashboard/components/EnergyAnalysisUploadButton";
import { useCrremEnergyInputUpload } from "features/energy-analysis-dashboard/hooks/useCrremEnergyInputUpload";
import { useTranslation } from "react-i18next";
import { parseJson } from "utils/dataManipulation";

import { useGetBuildingKpis, useGetKPIs, useGetProjectInfo } from "../../api/hooks/allHooks";
import { TextErrorBoundary } from "../ErrorBoundary";
import { KPIs } from "./KPIs";

type KPIAndStrandingPartProps = {
  projectId: string;
};

export const KPIAndStrandingPart = ({ projectId }: KPIAndStrandingPartProps) => {
  const { t } = useTranslation();
  const { data: projectInfo } = useGetProjectInfo(projectId, {});

  let crremScenario: string | undefined;
  if (projectInfo?.[0]?.crrem_scenario_list) {
    crremScenario = parseJson<string[]>(projectInfo?.[0]?.crrem_scenario_list)?.[0];
  }
  const crremKPIs = useGetKPIs(projectId, crremScenario);
  const { data: oldKpis, isLoading: kpiIsLoading } = useGetBuildingKpis(projectId);
  const { isUploading, uploadFile } = useCrremEnergyInputUpload(projectId);

  const kpis =
    crremKPIs.total_carbon_emission !== undefined && crremKPIs.total_energy_consumption !== undefined
      ? [crremKPIs]
      : oldKpis;

  const kpisLabelsAndKeysMapping = [
    {
      dataKey: "total_energy_consumption",
      label: t("BuildingProfile.kpis.totalEnergyConsumption"),
      round: true,
      unit: "kWh/m²/a",
    },
    {
      dataKey: "total_carbon_emission",
      label: t("BuildingProfile.kpis.totalCarbonEmission"),
      round: true,
      unit: "kg/m²/a",
    },
    {
      dataKey: "stranding_year",
      label: t("BuildingProfile.kpis.anticipatedStrandingYear"),
      round: false,
      unit: "",
    },
  ];
  return (
    <div className="relative">
      <Stack className="absolute right-0 top-0 z-10 gap-2">
        <EnergyAnalysisDashboard projectId={projectId} />
        <EnergyAnalysisUploadButton disabled={isUploading} onUpload={uploadFile} projectId={projectId} />
      </Stack>
      <TextErrorBoundary>
        <CrremPathwayOrLegacyChart
          className="min-h-[53.5rem] w-full border-none bg-transparent"
          projectId={projectId}
          title={t("BuildingProfile.kpis.crremM2Gross")}
        />
        <KPIs
          className="-mx-3 mt-4"
          isLoading={kpiIsLoading}
          kpis={kpis || []}
          kpisLabelsAndKeysMapping={kpisLabelsAndKeysMapping}
          paperClassName="!border-0 -p-6"
          xl2={4}
          xs={4}
        />
      </TextErrorBoundary>
    </div>
  );
};
